import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, BellRing, Scale, X } from 'lucide-react';
import { Button } from '../ui/button';
import { formatDay } from './EEOCCycleBanner';
import { timeAgo } from '../quality/qualityConfig';

export const EEOC_NUDGE_KEY = 'eeoc_nudge_dismissed';

const readDismissed = () => {
    try { return JSON.parse(localStorage.getItem(EEOC_NUDGE_KEY) || '[]'); } catch { return []; }
};

export const EEOCNudgePopup = ({ cycle, blocked }) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [dismissed, setDismissed] = useState(readDismissed);
    const [ready, setReady] = useState(false);
    useEffect(() => { const t = setTimeout(() => setReady(true), 800); return () => clearTimeout(t); }, []);

    const reminder = cycle?.reminder;
    const visible = ready && !blocked && Boolean(reminder?.pending) && !pathname.startsWith('/eeoc-training') && !dismissed.includes(reminder.token);

    const close = () => {
        const next = [...new Set([...dismissed, reminder.token])];
        localStorage.setItem(EEOC_NUDGE_KEY, JSON.stringify(next));
        setDismissed(next);
    };
    const open = () => { close(); navigate('/eeoc-training'); };

    return (
        <AnimatePresence>
            {visible && (
                <motion.div role="dialog" aria-label="EEOC refresher reminder"
                    initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16, scale: 0.98 }}
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                    className="pointer-events-auto w-full overflow-hidden rounded-2xl border border-red-500/30 bg-card/95 shadow-2xl backdrop-blur-md"
                    data-testid="eeoc-nudge-popup">
                    <div className="h-1 bg-red-500" />
                    <div className="space-y-4 p-5">
                        <div className="flex items-start gap-3">
                            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-red-500/10 text-red-600 dark:text-red-400">
                                <Scale className="h-5 w-5" />
                            </span>
                            <div className="min-w-0 flex-1">
                                <p className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-red-600 dark:text-red-400" data-testid="eeoc-nudge-popup-from">
                                    <BellRing className="h-3 w-3 origin-top animate-[wiggle_1s_ease-in-out_3]" /> Reminder from {reminder.last_nudged_by_name}
                                </p>
                                <h3 className="font-heading text-lg font-bold leading-tight" data-testid="eeoc-nudge-popup-title">Your EEOC refresher is overdue</h3>
                            </div>
                            <button type="button" onClick={close} aria-label="Close" data-testid="close-eeoc-nudge-popup-btn"
                                className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <p className="text-sm text-muted-foreground" data-testid="eeoc-nudge-popup-text">
                            You missed the <b className="text-foreground">{cycle.previous?.label}</b> refresher. Pass all {cycle.total_modules} modules by{' '}
                            <b className="text-foreground">{formatDay(cycle.due_date)}</b> to get back on track.
                        </p>
                        <p className="flex items-center justify-between rounded-xl border bg-background/70 px-3 py-2 text-xs text-muted-foreground">
                            <span data-testid="eeoc-nudge-popup-progress"><b className="text-foreground">{cycle.modules_passed}/{cycle.total_modules}</b> modules passed this {cycle.unit}</span>
                            <span>{timeAgo(reminder.last_nudged_at)}</span>
                        </p>
                        <Button onClick={open} className="w-full bg-red-600 text-white hover:bg-red-700" data-testid="open-eeoc-nudge-popup-btn">
                            Open EEOC Training <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
